import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Sparkles, Image as ImageIcon, AlertTriangle, CheckCircle2, Info, X, Film, Clapperboard, User } from "lucide-react";
import { MediaFrame } from "./MediaFrame";
import { parseScenesFromPrompt } from "@/domain/sceneEngine";

/**
 * Serializa los nodos del editor contentEditable a texto plano con menciones @startframe y @endframe
 */
const serializeEditorNodes = (root) => {
  if (!root) return "";
  let text = "";
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.nodeValue;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.dataset && node.dataset.mention) {
        text += node.dataset.mention;
      } else if (node.tagName === "BR") {
        text += "\n";
      } else {
        const isBlock = ["DIV", "P"].includes(node.tagName);
        if (isBlock && text.length > 0 && !text.endsWith("\n")) {
          text += "\n";
        }
        for (let child of node.childNodes) {
          walk(child);
        }
      }
    }
  };
  for (let child of root.childNodes) {
    walk(child);
  }
  return text;
};

/**
 * PromptMentionEditor
 * Editor de prompts enriquecido con chips inline interactivos (@startframe y @endframe).
 * Permite escribir antes y después de cada chip, conservando el cursor, la edición estable
 * y la serialización exacta al texto que requiere el backend.
 */
export const PromptMentionEditor = ({
  value = "",
  onChange,
  placeholder = "Describe tu vídeo...",
  rows = 5,
  className = "",
  testId = "prompt-mention-editor",
  startAsset = null,
  endAsset = null,
  supportsFLF = false,
  supportsScenes = false,
  supportsFrames = true,
  characters = [],
  characterReferences = [],
  assetById = {},
  defaultSceneDuration = 3,
  onPickStart = null,
  onPickEnd = null,
  onMentionInsert = null,
  onSelectCharacterMention = null,
  disabled = false,
  maxChars = null,
  modelLabel = "",
}) => {
  const editorRef = useRef(null);
  const isInternalChangeRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [inspectAsset, setInspectAsset] = useState(null); // { role: "start" | "end", asset: object }
  const [mentionQuery, setMentionQuery] = useState("");
  const [menuPos, setMenuPos] = useState({ top: 32, left: 16 });

  const charCount = (value || "").length;
  const isApproachingLimit = maxChars ? charCount >= Math.floor(maxChars * 0.8) && charCount <= maxChars : false;
  const isOverLimit = maxChars ? charCount > maxChars : false;

  // Detección de menciones en el texto para los indicadores secundarios
  const hasStartMention = /@startframe\b/i.test(value);
  const hasEndMention = /@endframe\b/i.test(value);

  // Opciones de mención disponibles según contexto
  const availableOptions = useMemo(() => {
    const opts = [];
    if (supportsScenes) {
      opts.push({
        tag: "@Escena",
        label: "Escena",
        role: "scene",
        isLinked: true,
        desc: "Nueva toma con duración y encuadre cinematográfico",
      });
    }
    if (supportsFrames) {
      opts.push({
        tag: "@startframe",
        label: "Fotograma inicial (Start Frame)",
        role: "start",
        asset: startAsset,
        isLinked: Boolean(startAsset),
        desc: startAsset ? (startAsset.label || startAsset.filename || "Imagen vinculada") : "Sin imagen asignada (no vinculada)",
      });
      if (supportsFLF || endAsset) {
        opts.push({
          tag: "@endframe",
          label: "Fotograma final (End Frame)",
          role: "end",
          asset: endAsset,
          isLinked: Boolean(endAsset),
          desc: endAsset ? (endAsset.label || endAsset.filename || "Imagen vinculada") : "Sin imagen asignada",
        });
      }
    }
    if (Array.isArray(characters) && characters.length > 0) {
      characters.forEach((c) => {
        const rawHandle = c?.handle || c?.name || "";
        const cleanHandle = rawHandle.replace(/^@+/, "").trim();
        if (!cleanHandle) return;
        const tag = `@${cleanHandle}`;
        const charAsset = c.masterAssetId ? assetById?.[c.masterAssetId] : null;
        opts.push({
          tag,
          label: c.name ? `${c.name} (@${cleanHandle})` : `@${cleanHandle}`,
          role: "character",
          characterId: c.id,
          character: c,
          asset: charAsset,
          assetId: c.masterAssetId,
          isLinked: Boolean(c.masterAssetId),
          desc: c.description || (c.outfit ? `Vestuario: ${c.outfit}` : "Vistas guardadas en Character Sheet"),
          badge: c.kind || "character",
        });
      });
    }
    return opts;
  }, [supportsScenes, supportsFrames, startAsset, endAsset, supportsFLF, characters, assetById]);

  // Personajes guardados presentes actualmente en el texto del prompt
  const detectedCharacters = useMemo(() => {
    if (!value || !Array.isArray(characters) || characters.length === 0) return [];
    return characters.filter((c) => {
      const rawHandle = c?.handle || c?.name || "";
      if (!rawHandle) return false;
      const cleanHandle = rawHandle.replace(/^@+/, "").trim().toLowerCase();
      const regex = new RegExp(`(?:^|[^a-zA-Z0-9_])@${cleanHandle}(?:[^a-zA-Z0-9_]|$)`, "i");
      return regex.test(value);
    });
  }, [value, characters]);

  // Filtrado de opciones si el usuario escribe después del @
  const filteredOptions = useMemo(() => {
    if (!mentionQuery) return availableOptions;
    const q = mentionQuery.toLowerCase();
    return availableOptions.filter((o) => {
      if (o.tag.toLowerCase().includes(q) || o.label.toLowerCase().includes(q)) return true;
      if (o.character?.name && o.character.name.toLowerCase().includes(q)) return true;
      if (o.character?.handle && o.character.handle.toLowerCase().includes(q)) return true;
      if (o.role === "scene" && ("escena".includes(q) || "scene".includes(q) || "toma".includes(q) || "shot".includes(q))) return true;
      return false;
    });
  }, [availableOptions, mentionQuery]);

  // Construir mapa de personajes por handle en minúsculas (sin @)
  const characterByHandle = useMemo(() => {
    const map = new Map();
    if (Array.isArray(characters)) {
      for (const c of characters) {
        if (!c) continue;
        const rawHandle = c.handle || c.name || "";
        const clean = rawHandle.replace(/^@+/, "").trim().toLowerCase();
        if (clean) {
          map.set(clean, c);
        }
      }
    }
    return map;
  }, [characters]);

  // Vistas adjuntas agrupadas por handle
  const attachedViewsByHandle = useMemo(() => {
    const map = new Map();
    if (Array.isArray(characterReferences)) {
      for (const cr of characterReferences) {
        if (!cr) continue;
        let handle = cr.characterHandle;
        if (!handle && cr.characterId) {
          const c = (characters || []).find((x) => x.id === cr.characterId);
          if (c) handle = c.handle;
        }
        if (handle) {
          const clean = handle.replace(/^@+/, "").trim().toLowerCase();
          const list = map.get(clean) || [];
          list.push(cr);
          map.set(clean, list);
        }
      }
    }
    return map;
  }, [characterReferences, characters]);

  // Regex para detectar tokens de menciones (startframe, endframe y handles de personajes conocidos)
  const buildSplitRegex = useCallback(() => {
    const tokens = [];
    if (supportsFrames) {
      tokens.push("@startframe", "@endframe");
    } else {
      if (/@startframe\b/i.test(value)) tokens.push("@startframe");
      if (/@endframe\b/i.test(value)) tokens.push("@endframe");
    }
    const handles = Array.from(characterByHandle.keys());
    if (handles.length > 0) {
      handles.sort((a, b) => b.length - a.length);
      for (const h of handles) {
        tokens.push(`@${h.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}`);
      }
    }
    if (tokens.length === 0) {
      return /(?!)/gi;
    }
    return new RegExp(`(${tokens.join("|")})(?![a-zA-Z0-9_])`, "gi");
  }, [supportsFrames, value, characterByHandle]);

  // Construir el elemento DOM del chip inline para fotogramas de vídeo
  const createChipElement = useCallback((tag, role, asset) => {
    const chip = document.createElement("span");
    chip.contentEditable = "false";
    chip.setAttribute("data-mention", tag);
    chip.setAttribute("data-role", role);
    chip.className = `inline-mention-chip inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[11px] align-baseline select-none cursor-pointer mx-1 border shadow-sm transition-all ${
      role === "start"
        ? asset
          ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
          : "border-amber-500/40 bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"
        : asset
        ? "border-purple-500/40 bg-purple-500/15 text-purple-300 hover:bg-purple-500/25"
        : "border-purple-900/40 bg-purple-950/25 text-purple-400 hover:bg-purple-900/40"
    }`;

    // Icono
    const iconSpan = document.createElement("span");
    iconSpan.className = "flex items-center opacity-90";
    iconSpan.innerHTML = role === "start"
      ? '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72Z"/><path d="m14 7 3 3"/></svg>'
      : '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
    chip.appendChild(iconSpan);

    // Texto de la mención
    const textSpan = document.createElement("span");
    textSpan.className = "font-semibold tracking-tight";
    textSpan.textContent = tag;
    chip.appendChild(textSpan);

    // Miniatura o dot de estado
    if (asset && asset.url) {
      const thumb = document.createElement("img");
      thumb.src = asset.url;
      thumb.alt = "";
      thumb.className = "h-3.5 w-3.5 rounded object-cover border border-white/20";
      chip.appendChild(thumb);
    } else {
      const dot = document.createElement("span");
      dot.className = `h-1.5 w-1.5 rounded-full ${
        asset
          ? role === "start" ? "bg-emerald-400" : "bg-purple-400"
          : "bg-amber-400 animate-pulse"
      }`;
      chip.appendChild(dot);
    }

    // Clic en el chip para inspeccionar el asset
    chip.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setInspectAsset({ role, asset });
    };

    return chip;
  }, []);

  // Construir el elemento DOM integrado para menciones de personajes (@handle)
  const createCharacterChipElement = useCallback(
    (character, attachedViews = []) => {
      const cleanHandle = (character?.handle || "").replace(/^@+/, "").trim();
      const chip = document.createElement("span");
      chip.contentEditable = "false";
      chip.setAttribute("data-mention", `@${cleanHandle}`);
      chip.setAttribute("data-role", "character");
      chip.setAttribute("data-handle", cleanHandle);
      chip.setAttribute("data-testid", `prompt-mention-chip-${cleanHandle}`);

      const hasViews = Array.isArray(attachedViews) && attachedViews.length > 0;

      chip.className = `inline-mention-chip inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono text-[11px] align-baseline select-none cursor-pointer mx-1 border shadow-sm transition-all ${
        hasViews
          ? "border-purple-500/40 bg-purple-500/15 text-purple-200 hover:bg-purple-500/25 hover:border-purple-500/60"
          : "border-[#383838] bg-[#1c1c1c] text-[#a3a3a3] hover:border-[#505050] hover:text-[#ededed]"
      }`;

      // Contenedor de icono / miniatura
      const iconContainer = document.createElement("span");
      iconContainer.className = "relative flex items-center justify-center shrink-0";

      const fallbackIcon = document.createElement("span");
      fallbackIcon.className = `flex h-4 w-4 items-center justify-center rounded-full shrink-0 ${
        hasViews ? "bg-purple-500/30 text-purple-200" : "bg-[#282828] text-[#888]"
      }`;
      fallbackIcon.innerHTML =
        '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';

      if (hasViews) {
        const firstView = attachedViews[0];
        const asset = assetById?.[firstView.assetId];
        if (asset && asset.url) {
          const img = document.createElement("img");
          img.src = asset.url;
          img.alt = "";
          img.className = "h-4 w-4 rounded-full object-cover border border-purple-400/40 shrink-0";
          img.setAttribute("data-testid", `prompt-chip-thumb-${cleanHandle}`);
          img.onerror = () => {
            // Si falla la carga de la miniatura, conserva legibles el handle y su estado
            img.style.display = "none";
            fallbackIcon.style.display = "flex";
          };
          fallbackIcon.style.display = "none";
          iconContainer.appendChild(img);
          iconContainer.appendChild(fallbackIcon);
        } else {
          iconContainer.appendChild(fallbackIcon);
        }
      } else {
        // Sin vistas adjuntas: icono neutro explícito (NUNCA mostrar imagen principal del personaje)
        iconContainer.appendChild(fallbackIcon);
      }
      chip.appendChild(iconContainer);

      // Texto @handle
      const textSpan = document.createElement("span");
      textSpan.className = "font-semibold tracking-tight";
      textSpan.textContent = `@${cleanHandle}`;
      chip.appendChild(textSpan);

      // Estado / Indicador
      if (hasViews) {
        if (attachedViews.length > 1) {
          const plusBadge = document.createElement("span");
          plusBadge.className = "text-[9px] font-bold px-1 rounded-full bg-purple-500/30 text-purple-200 border border-purple-400/40";
          plusBadge.textContent = `+${attachedViews.length - 1}`;
          plusBadge.setAttribute("data-testid", `prompt-chip-plus-${cleanHandle}`);
          chip.appendChild(plusBadge);
        }
        chip.title = `@${cleanHandle} (${attachedViews.length} vista${attachedViews.length > 1 ? "s" : ""} adjunta${attachedViews.length > 1 ? "s" : ""}). Clic para cambiar vistas.`;
      } else {
        const noRefSpan = document.createElement("span");
        noRefSpan.className = "text-[9px] px-1.5 py-0.2 rounded bg-[#252525] text-[#8e8e8e] font-sans border border-[#333]";
        noRefSpan.textContent = "Sin referencia visual";
        noRefSpan.setAttribute("data-testid", `prompt-chip-status-${cleanHandle}`);
        chip.appendChild(noRefSpan);
        chip.title = `@${cleanHandle}: Sin referencia visual. Clic para elegir vistas guardadas.`;
      }

      // Al pulsar la etiqueta, abrir el selector de vistas existente
      chip.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelectCharacterMention?.(character);
      };

      return chip;
    },
    [assetById, onSelectCharacterMention]
  );

  // Función para poblar el DOM con texto y chips
  const populateDOMFromText = useCallback(
    (text) => {
      const el = editorRef.current;
      if (!el) return;
      el.innerHTML = "";

      if (!text) return;

      const appendNormalText = (target, str) => {
        const lines = str.split("\n");
        lines.forEach((line, idx) => {
          if (idx > 0) {
            target.appendChild(document.createElement("br"));
          }
          if (line) {
            target.appendChild(document.createTextNode(line));
          }
        });
      };

      const regex = buildSplitRegex();
      const parts = text.split(regex);

      parts.forEach((part) => {
        if (!part) return;
        const lower = part.toLowerCase();
        if (lower === "@startframe") {
          el.appendChild(createChipElement("@startframe", "start", startAsset));
        } else if (lower === "@endframe") {
          el.appendChild(createChipElement("@endframe", "end", endAsset));
        } else if (lower.startsWith("@")) {
          const handle = lower.slice(1);
          const charObj = characterByHandle.get(handle);
          if (charObj) {
            const attached = attachedViewsByHandle.get(handle) || [];
            el.appendChild(createCharacterChipElement(charObj, attached));
          } else {
            appendNormalText(el, part);
          }
        } else {
          appendNormalText(el, part);
        }
      });
    },
    [buildSplitRegex, createChipElement, createCharacterChipElement, startAsset, endAsset, characterByHandle, attachedViewsByHandle]
  );

  // Actualizar chips de personajes existentes en el DOM cuando cambian las referencias adjuntas
  const updateChipsInDOM = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const charChips = el.querySelectorAll('span.inline-mention-chip[data-role="character"]');
    charChips.forEach((chip) => {
      const handle = chip.getAttribute("data-handle");
      if (!handle) return;
      const clean = handle.replace(/^@+/, "").trim().toLowerCase();
      const charObj = characterByHandle.get(clean);
      if (charObj) {
        const attached = attachedViewsByHandle.get(clean) || [];
        const newChip = createCharacterChipElement(charObj, attached);
        chip.replaceWith(newChip);
      }
    });
  }, [characterByHandle, attachedViewsByHandle, createCharacterChipElement]);

  useEffect(() => {
    updateChipsInDOM();
  }, [characterReferences, assetById, updateChipsInDOM]);

  // Sincronizar value externo con el contenido del editor sólo si no es una edición interna
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    populateDOMFromText(value);
  }, [value, populateDOMFromText]);

  // Definir getter .value y .placeholder en el elemento DOM para compatibilidad total con pruebas y utilidades
  useEffect(() => {
    const el = editorRef.current;
    if (el) {
      Object.defineProperty(el, "value", {
        get: () => value,
        set: (v) => {
          onChange?.(v);
        },
        configurable: true,
      });
      Object.defineProperty(el, "placeholder", {
        get: () => placeholder,
        configurable: true,
      });
    }
  }, [value, placeholder, onChange]);

  // Insertar mención en la posición actual del cursor o al final
  const insertMention = useCallback((tag, option = null) => {
    const el = editorRef.current;
    if (!el) return;

    el.focus();
    const sel = window.getSelection();
    let range = null;

    if (sel && sel.rangeCount > 0) {
      range = sel.getRangeAt(0);
      // Asegurarse de que el rango esté dentro de nuestro editor
      if (!el.contains(range.commonAncestorContainer)) {
        range = null;
      }
    }

    const isScene = tag === "@Escena" || tag.toLowerCase().startsWith("@escena") || tag.toLowerCase().startsWith("@scene");

    if (isScene) {
      const currentText = serializeEditorNodes(el);
      const parsed = parseScenesFromPrompt(currentText);
      let nextNum = 1;
      if (parsed.scenes && parsed.scenes.length > 0) {
        const maxIdx = Math.max(...parsed.scenes.map((s) => s.index || 0));
        nextNum = maxIdx + 1;
      }
      const dur = defaultSceneDuration || 3;

      if (range) {
        const node = range.startContainer;
        let textBeforeAt = "";
        if (node.nodeType === Node.TEXT_NODE) {
          const textBefore = node.nodeValue.slice(0, range.startOffset);
          const atIdx = textBefore.lastIndexOf("@");
          if (atIdx !== -1) {
            textBeforeAt = textBefore.slice(0, atIdx);
            node.nodeValue = textBeforeAt + node.nodeValue.slice(range.startOffset);
            range.setStart(node, atIdx);
            range.setEnd(node, atIdx);
          }
        }

        const needsLeadingNewline = textBeforeAt.trim().length > 0 && !textBeforeAt.endsWith("\n");
        const headerText = `${needsLeadingNewline ? "\n\n" : ""}@Escena ${nextNum} [${dur}s]\n`;
        const textNode = document.createTextNode(headerText);

        range.deleteContents();
        range.insertNode(textNode);

        const newRange = document.createRange();
        newRange.setStartAfter(textNode);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      } else {
        const needsLeadingNewline = currentText.trim().length > 0 && !currentText.endsWith("\n");
        const headerText = `${needsLeadingNewline ? "\n\n" : ""}@Escena ${nextNum} [${dur}s]\n`;
        const textNode = document.createTextNode(headerText);
        el.appendChild(textNode);

        if (sel) {
          const newRange = document.createRange();
          newRange.setStartAfter(textNode);
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
        }
      }

      isInternalChangeRef.current = true;
      const serialized = serializeEditorNodes(el);
      onChange?.(serialized);
      onMentionInsert?.(`@Escena ${nextNum}`);

      setMenuOpen(false);
      setMentionQuery("");
      return;
    }

    if (tag !== "@startframe" && tag !== "@endframe") {
      const cleanHandle = tag.toLowerCase().replace(/^@+/, "").trim();
      const charObj = option?.character || characterByHandle.get(cleanHandle);
      if (charObj) {
        const attached = attachedViewsByHandle.get((charObj.handle || "").replace(/^@+/, "").trim().toLowerCase()) || [];
        const chip = createCharacterChipElement(charObj, attached);

        if (range) {
          const node = range.startContainer;
          if (node.nodeType === Node.TEXT_NODE) {
            const textBefore = node.nodeValue.slice(0, range.startOffset);
            const atIdx = textBefore.lastIndexOf("@");
            if (atIdx !== -1) {
              node.nodeValue = textBefore.slice(0, atIdx) + node.nodeValue.slice(range.startOffset);
              range.setStart(node, atIdx);
              range.setEnd(node, atIdx);
            }
          }
          range.deleteContents();
          range.insertNode(chip);

          const spaceNode = document.createTextNode(" ");
          if (chip.nextSibling) {
            el.insertBefore(spaceNode, chip.nextSibling);
          } else {
            el.appendChild(spaceNode);
          }

          const newRange = document.createRange();
          newRange.setStartAfter(spaceNode);
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
        } else {
          el.appendChild(chip);
          const spaceNode = document.createTextNode(" ");
          el.appendChild(spaceNode);

          if (sel) {
            const newRange = document.createRange();
            newRange.setStartAfter(spaceNode);
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);
          }
        }

        isInternalChangeRef.current = true;
        const serialized = serializeEditorNodes(el);
        onChange?.(serialized);
        onMentionInsert?.(tag);
        onSelectCharacterMention?.(charObj);

        setMenuOpen(false);
        setMentionQuery("");
        return;
      }

      const textToInsert = `${tag} `;
      if (range) {
        const node = range.startContainer;
        if (node.nodeType === Node.TEXT_NODE) {
          const textBefore = node.nodeValue.slice(0, range.startOffset);
          const atIdx = textBefore.lastIndexOf("@");
          if (atIdx !== -1) {
            node.nodeValue = textBefore.slice(0, atIdx) + node.nodeValue.slice(range.startOffset);
            range.setStart(node, atIdx);
            range.setEnd(node, atIdx);
          }
        }
        range.deleteContents();
        const textNode = document.createTextNode(textToInsert);
        range.insertNode(textNode);
        const newRange = document.createRange();
        newRange.setStartAfter(textNode);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      } else {
        const textNode = document.createTextNode(textToInsert);
        el.appendChild(textNode);
        if (sel) {
          const newRange = document.createRange();
          newRange.setStartAfter(textNode);
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
        }
      }
      isInternalChangeRef.current = true;
      const serialized = serializeEditorNodes(el);
      onChange?.(serialized);
      onMentionInsert?.(tag);

      setMenuOpen(false);
      setMentionQuery("");
      return;
    }

    const role = tag === "@startframe" ? "start" : "end";
    const asset = role === "start" ? startAsset : endAsset;
    const chip = createChipElement(tag, role, asset);

    if (range) {
      // Si se estaba escribiendo después de un '@', eliminar ese fragmento
      const node = range.startContainer;
      if (node.nodeType === Node.TEXT_NODE) {
        const textBefore = node.nodeValue.slice(0, range.startOffset);
        const atIdx = textBefore.lastIndexOf("@");
        if (atIdx !== -1) {
          node.nodeValue = textBefore.slice(0, atIdx) + node.nodeValue.slice(range.startOffset);
          range.setStart(node, atIdx);
          range.setEnd(node, atIdx);
        }
      }

      range.deleteContents();
      range.insertNode(chip);

      // Insertar espacio después del chip para continuar escribiendo con naturalidad
      const spaceNode = document.createTextNode(" ");
      if (chip.nextSibling) {
        el.insertBefore(spaceNode, chip.nextSibling);
      } else {
        el.appendChild(spaceNode);
      }

      // Mover el cursor después del espacio
      const newRange = document.createRange();
      newRange.setStartAfter(spaceNode);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    } else {
      // Si no hay rango activo, insertar al final
      el.appendChild(chip);
      const spaceNode = document.createTextNode(" ");
      el.appendChild(spaceNode);

      if (sel) {
        const newRange = document.createRange();
        newRange.setStartAfter(spaceNode);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      }
    }

    // Serializar y notificar cambio
    isInternalChangeRef.current = true;
    const serialized = serializeEditorNodes(el);
    onChange?.(serialized);
    onMentionInsert?.(tag);

    setMenuOpen(false);
    setMentionQuery("");
  }, [createChipElement, createCharacterChipElement, startAsset, endAsset, onChange, onMentionInsert, defaultSceneDuration, onSelectCharacterMention, characterByHandle, attachedViewsByHandle]);

  // Manejador del evento input sobre el contentEditable
  const handleInput = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;

    // Serializar texto actual
    const serialized = serializeEditorNodes(el);
    isInternalChangeRef.current = true;
    onChange?.(serialized);

    // Detectar si el usuario está escribiendo una mención con '@'
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const node = range.startContainer;
      if (node.nodeType === Node.TEXT_NODE) {
        const textBefore = node.nodeValue.slice(0, range.startOffset);
        // Activar sólo si @ está al inicio o tras un espacio/salto de línea (evitar correos tipo user@dominio)
        const atMatch = textBefore.match(/(?:^|\s)@([a-zA-Z0-9_\u00C0-\u017F-]*)$/);
        if (atMatch) {
          setMentionQuery(atMatch[1] || "");
          setMenuOpen(true);
          setSelectedIndex(0);

          // Calcular posición de la ventana flotante
          try {
            const rect = range.getBoundingClientRect();
            const parentRect = el.getBoundingClientRect();
            if (rect && parentRect) {
              setMenuPos({
                top: Math.max(30, rect.bottom - parentRect.top + 8),
                left: Math.max(12, rect.left - parentRect.left),
              });
            }
          } catch (_) {
            // fallback pos
          }
          return;
        }
      }
    }
    setMenuOpen(false);
  }, [onChange]);

  // Manejar teclado para el menú de autocompletado y atajos
  const handleKeyDown = (e) => {
    if (menuOpen && filteredOptions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredOptions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const selectedOpt = filteredOptions[selectedIndex] || filteredOptions[0];
        if (selectedOpt) {
          insertMention(selectedOpt.tag, selectedOpt);
        }
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMenuOpen(false);
        return;
      }
    }
  };

  // Manejar pegado para asegurar que solo se inserte texto plano y chips de menciones reconocidas
  const handlePaste = (e) => {
    e.preventDefault();
    const pasteText = (e.clipboardData || window.clipboardData).getData("text/plain");
    if (!pasteText) return;

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    range.deleteContents();

    const appendNormalText = (target, str) => {
      const lines = str.split("\n");
      lines.forEach((line, idx) => {
        if (idx > 0) target.appendChild(document.createElement("br"));
        if (line) target.appendChild(document.createTextNode(line));
      });
    };

    const regex = buildSplitRegex();
    const parts = pasteText.split(regex);
    const fragment = document.createDocumentFragment();

    parts.forEach((part) => {
      if (!part) return;
      const lower = part.toLowerCase();
      if (supportsFrames && lower === "@startframe") {
        fragment.appendChild(createChipElement("@startframe", "start", startAsset));
      } else if (supportsFrames && lower === "@endframe") {
        fragment.appendChild(createChipElement("@endframe", "end", endAsset));
      } else if (lower.startsWith("@")) {
        const handle = lower.slice(1);
        const charObj = characterByHandle.get(handle);
        if (charObj) {
          const attached = attachedViewsByHandle.get(handle) || [];
          fragment.appendChild(createCharacterChipElement(charObj, attached));
        } else {
          appendNormalText(fragment, part);
        }
      } else {
        appendNormalText(fragment, part);
      }
    });

    range.insertNode(fragment);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);

    handleInput();
  };

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (editorRef.current && !editorRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, []);

  return (
    <div className={`relative flex flex-col gap-2 ${className}`}>
      {/* Barra superior de atajos para referencias visuales */}
      {(supportsFrames || supportsScenes || supportsFLF) && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-1.5 text-[11px] text-[#8e8e8e]">
            <span>Menciones visuales:</span>
            {supportsFrames && (
              <button
                type="button"
                data-testid="insert-mention-startframe-btn"
                onClick={() => insertMention("@startframe")}
                className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-[11px] font-mono transition-colors border shadow-sm ${
                  startAsset
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                    : "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                }`}
                title={startAsset ? "Insertar chip @startframe (vinculado)" : "Insertar chip @startframe (requiere asignar imagen)"}
              >
                <Sparkles size={11} />
                <span>@startframe</span>
                {startAsset ? (
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                )}
              </button>
            )}

            {supportsScenes && (
              <button
                type="button"
                data-testid="insert-mention-scene-btn"
                onClick={() => insertMention("@Escena")}
                className="inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-[11px] font-mono transition-colors border shadow-sm border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                title="Insertar encabezado de escena (@Escena N [Xs])"
              >
                <Clapperboard size={11} />
                <span>+ @Escena</span>
              </button>
            )}

            {supportsFLF && (
              <button
                type="button"
                data-testid="insert-mention-endframe-btn"
                onClick={() => insertMention("@endframe")}
                className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-[11px] font-mono transition-colors border shadow-sm ${
                  endAsset
                    ? "border-purple-500/40 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20"
                    : "border-[#303030] bg-[#1a1a1a] text-[#a3a3a3] hover:border-[#404040]"
                }`}
                title={endAsset ? "Insertar chip @endframe (vinculado)" : "Insertar chip @endframe (requiere asignar imagen final)"}
              >
                <Film size={11} />
                <span>@endframe</span>
                {endAsset ? (
                  <span className="h-1.5 w-1.5 rounded-full bg-purple-400" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-[#525252]" />
                )}
              </button>
            )}
          </div>

          <span className="text-[10px] text-[#6b6b6b]">Escribe @ o pulsa el botón para insertar chips</span>
        </div>
      )}

      {/* Editor enriquecido contentEditable con soporte nativo de chips inline */}
      <div className="relative">
        <div
          ref={editorRef}
          data-testid={testId}
          role="textbox"
          aria-multiline="true"
          contentEditable={!disabled}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className={`min-h-[120px] w-full rounded-md border border-[#2e2e2e] bg-[#121212] p-3 text-sm text-[#e5e5e5] outline-none focus:border-[#4a4a4a] focus:ring-1 focus:ring-[#525252] overflow-y-auto font-sans leading-relaxed transition-colors select-text ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
          data-placeholder={placeholder}
          style={{ minHeight: `${rows * 24}px` }}
        />

        {/* Placeholder visual si está vacío */}
        {!value && (
          <div
            className="pointer-events-none absolute left-3 top-3 text-sm text-[#525252] select-none"
            aria-hidden="true"
          >
            {placeholder}
          </div>
        )}

        {/* Menú contextual flotante al escribir @ */}
        {menuOpen && filteredOptions.length > 0 && (
          <div
            data-testid="prompt-mention-dropdown"
            className="absolute z-50 w-72 rounded-lg border border-[#383838] bg-[#141414] p-1.5 shadow-2xl backdrop-blur-md"
            style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
          >
            <div className="mb-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
              Referencias disponibles
            </div>
            {filteredOptions.map((opt, i) => {
              const clean = (opt.character?.handle || opt.tag || "").replace(/^@+/, "");
              return (
                <button
                  key={opt.tag}
                  type="button"
                  data-testid={`mention-option-${opt.role}`}
                  data-mention-handle={clean}
                  onClick={() => insertMention(opt.tag, opt)}
                  className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-xs transition-colors ${
                    selectedIndex === i ? "bg-[#262626] text-[#ededed]" : "text-[#a3a3a3] hover:bg-[#1a1a1a]"
                  }`}
                >
                  <div className="relative h-7 w-7 flex-shrink-0 overflow-hidden rounded border border-[#333] bg-black/60 flex items-center justify-center">
                    {opt.role === "scene" ? (
                      <Clapperboard size={14} className="text-amber-400" />
                    ) : opt.asset ? (
                      <MediaFrame asset={opt.asset} className="h-full w-full" fit="cover" />
                    ) : opt.role === "character" ? (
                      <div className="flex h-full w-full items-center justify-center bg-[#242424] text-[#f5b544]">
                        <User size={14} />
                      </div>
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[#555]">
                        <ImageIcon size={14} />
                      </div>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-1.5 min-w-0 truncate">
                        {opt.role === "character" && opt.character?.name ? (
                          <>
                            <span className="font-medium text-[#f4f1ec] truncate">
                              {opt.character.name}
                            </span>
                            <span className="font-mono text-[11px] text-[#f5b544] shrink-0">
                              @{clean}
                            </span>
                          </>
                        ) : (
                          <span
                            className={`font-mono font-medium ${
                              opt.role === "scene"
                                ? "text-amber-400"
                                : opt.role === "start"
                                ? "text-emerald-400"
                                : opt.role === "character"
                                ? "text-[#f5b544]"
                                : "text-purple-400"
                            }`}
                          >
                            {opt.tag}
                          </span>
                        )}
                      </div>
                      {opt.role === "scene" ? (
                        <span className="rounded bg-amber-500/10 px-1.5 py-0.2 text-[9px] font-medium text-amber-400 shrink-0">
                          Escena
                        </span>
                      ) : opt.role === "character" ? (
                        <span className="rounded bg-[#f5b544]/15 px-1.5 py-0.2 text-[9px] font-medium text-[#f5b544] shrink-0">
                          {opt.badge || "Personaje"}
                        </span>
                      ) : opt.isLinked ? (
                        <span className="rounded bg-emerald-500/10 px-1.5 py-0.2 text-[9px] font-medium text-emerald-400 shrink-0">
                          Vinculado
                        </span>
                      ) : (
                        <span className="rounded bg-amber-500/10 px-1.5 py-0.2 text-[9px] font-medium text-amber-400 shrink-0">
                          Sin imagen
                        </span>
                      )}
                    </div>
                    <span className="truncate text-[10px] text-[#737373]">{opt.desc}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Barra de estado con contador de caracteres */}
      <div className="flex items-center justify-between px-1 text-[11px] text-[#737373]">
        <span>
          {supportsFrames || supportsScenes || supportsFLF
            ? "Escribe @ o usa los botones superiores para insertar referencias"
            : "Escribe @ para insertar personajes y referencias"}
        </span>
        {maxChars ? (
          <span
            data-testid="prompt-char-counter"
            className={`font-mono transition-colors ${
              isOverLimit
                ? "text-red-400 font-bold"
                : isApproachingLimit
                ? "text-amber-400 font-medium"
                : "text-[#737373]"
            }`}
          >
            {charCount.toLocaleString()} / {maxChars.toLocaleString()}
          </span>
        ) : null}
      </div>

      {/* Aviso de aproximación al límite */}
      {isApproachingLimit && (
        <div
          data-testid="prompt-length-notice"
          className="flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-300"
        >
          <AlertTriangle size={12} className="text-amber-400 flex-shrink-0" />
          <span>
            Te estás acercando al límite de {maxChars.toLocaleString()} caracteres admitido por {modelLabel || "este modelo"} ({charCount.toLocaleString()}/{maxChars.toLocaleString()}).
          </span>
        </div>
      )}

      {/* Alerta de bloqueo si supera el límite */}
      {isOverLimit && (
        <div
          data-testid="prompt-length-warning"
          className="flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 p-2.5 text-xs text-red-300"
        >
          <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
          <span>
            El prompt supera el límite de {maxChars.toLocaleString()} caracteres admitido por {modelLabel || "este modelo"} ({charCount.toLocaleString()}/{maxChars.toLocaleString()}). Por favor, reduce el texto antes de generar.
          </span>
        </div>
      )}

      {/* Indicadores secundarios de referencias detectadas en el prompt */}
      {supportsFrames && (hasStartMention || hasEndMention) && (
        <div data-testid="active-mentions-bar" className="flex flex-wrap items-center gap-2 pt-1">
          {hasStartMention && (
            <div
              data-testid="mention-tag-startframe"
              className={`flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs select-none transition-all ${
                startAsset
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                  : "border-red-500/40 bg-red-500/10 text-red-300"
              }`}
            >
              <div className="relative h-4 w-4 overflow-hidden rounded-full border border-black/40">
                {startAsset ? (
                  <MediaFrame asset={startAsset} className="h-full w-full" fit="cover" />
                ) : (
                  <AlertTriangle size={12} className="text-red-400" />
                )}
              </div>
              <span className="font-mono font-semibold">@startframe</span>
              <span className="text-[11px] opacity-80">
                {startAsset ? (
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle2 size={11} className="text-emerald-400" />
                    <span className="truncate max-w-[120px]">{startAsset.label || startAsset.filename || "Vinculado"}</span>
                  </span>
                ) : (
                  "⚠️ No resuelto (falta imagen)"
                )}
              </span>
              {startAsset && (
                <button
                  type="button"
                  data-testid="inspect-startframe-btn"
                  onClick={() => setInspectAsset({ role: "start", asset: startAsset })}
                  className="rounded px-1.5 py-0.5 text-[10px] text-emerald-400 hover:bg-emerald-500/20"
                  title="Inspeccionar recurso vinculado"
                >
                  <Info size={12} />
                </button>
              )}
            </div>
          )}

          {hasEndMention && (
            <div
              data-testid="mention-tag-endframe"
              className={`flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs select-none transition-all ${
                endAsset
                  ? "border-purple-500/40 bg-purple-500/10 text-purple-300"
                  : "border-red-500/40 bg-red-500/10 text-red-300"
              }`}
            >
              <div className="relative h-4 w-4 overflow-hidden rounded-full border border-black/40">
                {endAsset ? (
                  <MediaFrame asset={endAsset} className="h-full w-full" fit="cover" />
                ) : (
                  <AlertTriangle size={12} className="text-red-400" />
                )}
              </div>
              <span className="font-mono font-semibold">@endframe</span>
              <span className="text-[11px] opacity-80">
                {endAsset ? (
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle2 size={11} className="text-purple-400" />
                    <span className="truncate max-w-[120px]">{endAsset.label || endAsset.filename || "Vinculado"}</span>
                  </span>
                ) : (
                  "⚠️ No resuelto (falta fotograma final)"
                )}
              </span>
              {endAsset && (
                <button
                  type="button"
                  data-testid="inspect-endframe-btn"
                  onClick={() => setInspectAsset({ role: "end", asset: endAsset })}
                  className="rounded px-1.5 py-0.5 text-[10px] text-purple-400 hover:bg-purple-500/20"
                  title="Inspeccionar recurso vinculado"
                >
                  <Info size={12} />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Indicadores de personajes detectados en el prompt */}
      {detectedCharacters.length > 0 && (
        <div
          data-testid="prompt-detected-characters-row"
          className="flex flex-wrap items-center gap-1.5 pt-1 text-[11px]"
        >
          <span className="text-[#737373] text-[10px] font-medium">Personajes en el prompt:</span>
          {detectedCharacters.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`prompt-char-mention-btn-${c.handle}`}
              onClick={() => onSelectCharacterMention?.(c)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[10px] bg-purple-500/10 border border-purple-500/30 text-purple-300 hover:bg-purple-500/20 hover:border-purple-500/50 transition-colors"
              title={`Elegir o cambiar vistas guardadas de ${c.name || c.handle}`}
            >
              <span>@{c.handle}</span>
              <span className="text-[9px] text-purple-400/80 underline ml-0.5">Vistas</span>
            </button>
          ))}
        </div>
      )}

      {/* Popover / Tarjeta de inspección de asset vinculado */}
      {inspectAsset && (
        <div
          data-testid="asset-inspection-card"
          className="rounded-lg border border-[#383838] bg-[#121212] p-3 text-xs shadow-xl animate-in fade-in"
        >
          <div className="flex items-center justify-between pb-2 border-b border-[#262626]">
            <div className="flex items-center gap-2">
              <span className={`font-mono font-semibold ${inspectAsset.role === "start" ? "text-emerald-400" : "text-purple-400"}`}>
                @{inspectAsset.role === "start" ? "startframe" : "endframe"}
              </span>
              <span className="rounded bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
                Asset verificado
              </span>
            </div>
            <button
              type="button"
              data-testid="close-inspection-btn"
              onClick={() => setInspectAsset(null)}
              className="text-[#737373] hover:text-[#d4d4d4]"
            >
              <X size={14} />
            </button>
          </div>
          <div className="mt-2.5 flex items-start gap-3">
            <div className="relative h-20 w-28 flex-shrink-0 overflow-hidden rounded border border-[#303030] bg-black">
              <MediaFrame asset={inspectAsset.asset} className="h-full w-full" fit="contain" />
            </div>
            <div className="flex flex-col gap-1 min-w-0 flex-1 text-[11px] text-[#a3a3a3]">
              <div className="truncate font-medium text-[#ededed]">
                {inspectAsset.asset.label || inspectAsset.asset.filename || "Sin nombre"}
              </div>
              <div className="font-mono text-[10px] text-[#737373]">
                ID: <span className="text-[#a3a3a3]">{inspectAsset.asset.id}</span>
              </div>
              {inspectAsset.asset.width && inspectAsset.asset.height && (
                <div className="text-[10px]">
                  Dimensiones: {inspectAsset.asset.width} × {inspectAsset.asset.height}
                </div>
              )}
              <div className="mt-1 text-[10px] text-emerald-400/90 font-medium">
                ✓ Se enviará como {inspectAsset.role === "start" ? "source_asset_id" : "end_asset_id"} a APIMart.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
